import React, { useEffect, useMemo, useRef, useState } from "react";
import { intl, getToken } from "dmsbasereact";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Stack,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import { createSafeHtml } from "../../utils/sanitizeHtml";
import { DmsFileViewer } from "../DmsFileViewer/DmsFileViewer";
import { getBaseURL } from "../../build_config";

const styles = {
  manualContainer: {
    maxHeight: "60vh",
    overflowY: "auto",
    paddingRight: 2,
  },
  htmlStyles: {
    "& h1": { fontSize: "1.75rem", marginTop: 0 },
    "& h2": { fontSize: "1.35rem", marginTop: 3 },
    "& h3": { fontSize: "1.15rem", marginTop: 2 },
    "& table": {
      width: "100%",
      borderCollapse: "collapse",
      marginY: 2,
    },
    "& th, & td": {
      border: "1px solid #e0e0e0",
      padding: "8px",
      textAlign: "left",
    },
    "& ul": { paddingLeft: 3 },
    "& ol": { paddingLeft: 3 },
    "& code": {
      backgroundColor: "#f5f5f5",
      padding: "2px 4px",
      borderRadius: "4px",
      fontSize: "0.85rem",
    },
  },
};

const ManualCard = ({ manual, onView, onDownload }) => {
  const hasFile = manual?.file_pk && manual?.file_name;
  const canDownload = hasFile && manual?.allow_download !== false;

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <DescriptionIcon color="primary" />
          <Typography variant="h6" component="div" noWrap>
            {manual.title || manual.file_name || intl.get("helpCenter.untitledManual").d("Untitled Manual")}
          </Typography>
        </Stack>
      </CardContent>
      <CardActions>
        <Button size="small" onClick={() => onView(manual)}>
          {intl.get("helpCenter.viewManual").d("View")}
        </Button>
        {canDownload && (
          <Button size="small" startIcon={<DownloadIcon />} onClick={() => onDownload(manual)}>
            {intl.get("helpCenter.download").d("Download")}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

const ManualViewer = ({ manual, onBack, onDownload }) => {
  const containerRef = useRef(null);
  const anchors = useMemo(() => manual?.anchors || [], [manual]);
  const [anchorId, setAnchorId] = useState(null);

  const hasFile = manual?.file_pk && manual?.file_name;
  const canDownload = hasFile && manual?.allow_download !== false;

  useEffect(() => {
    if (!anchorId || !containerRef.current) {
      return;
    }
    const target = containerRef.current.querySelector(`#${anchorId}`);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      containerRef.current.scrollTop = 0;
    }
  }, [anchorId, manual?.html]);

  // Render file viewer if we have a file (PDF, image, video, etc.)
  if (hasFile) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button variant="text" onClick={onBack}>
            {intl.get("helpCenter.backToList").d("← Back")}
          </Button>
          <Typography variant="h6">
            {manual.title || manual.file_name}
          </Typography>
          {canDownload ? (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => onDownload(manual)}
            >
              {intl.get("helpCenter.downloadFile").d("Download File")}
            </Button>
          ) : (
            <Box />
          )}
        </Box>

        <DmsFileViewer
          filePk={manual.file_pk}
          fileName={manual.file_name}
          height="60vh"
        />
      </Box>
    );
  }

  // Render HTML content
  return (
    <Box>
      <Button variant="text" onClick={onBack} sx={{ mb: 2 }}>
        {intl.get("helpCenter.backToList").d("← Back")}
      </Button>
      <Grid container spacing={2}>
        <Grid item xs={12} md={anchors.length > 0 ? 3 : 12}>
          {anchors.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {intl.get("helpCenter.manualTOC").d("Manual Sections")}
              </Typography>
              <List dense sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                {anchors.map((anchor) => (
                  <ListItemButton
                    key={anchor.id}
                    selected={anchor.id === anchorId}
                    onClick={() => setAnchorId(anchor.id)}
                  >
                    <ListItemText primary={anchor.name} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </Grid>
        <Grid item xs={12} md={anchors.length > 0 ? 9 : 12}>
          <Box ref={containerRef} sx={{ ...styles.manualContainer, ...styles.htmlStyles }}>
            <div dangerouslySetInnerHTML={createSafeHtml(manual.html)} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export const DmsHelpCenterManualTab = ({
  manuals = [],
  loading,
  error,
  anchorId,
  onSelectAnchor,
}) => {
  const [selectedManual, setSelectedManual] = useState(null);

  const handleDownload = async (manual) => {
    if (!manual?.file_pk) return;

    try {
      const token = getToken();
      const response = await fetch(`${getBaseURL()}/help-center/file/${manual.file_pk}`, {
        headers: { 'Authorization': `Bearer ${token.access_token}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = manual.file_name || 'manual';
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 4 }}>
        {intl
          .get("helpCenter.manualError")
          .d("Could not load manual.")}
      </Typography>
    );
  }

  // Check if we have any manuals
  if (!manuals || manuals.length === 0) {
    return (
      <Typography sx={{ py: 4 }}>
        {intl
          .get("helpCenter.manualEmpty")
          .d("No content available for the manual at this time.")}
      </Typography>
    );
  }

  // If a manual is selected, show the viewer
  if (selectedManual) {
    return (
      <ManualViewer
        manual={selectedManual}
        onBack={() => setSelectedManual(null)}
        onDownload={handleDownload}
      />
    );
  }

  // If only one manual, show it directly
  if (manuals.length === 1) {
    const manual = manuals[0];
    const hasFile = manual?.file_pk && manual?.file_name;
    const canDownload = hasFile && manual?.allow_download !== false;

    // Render file viewer if we have a file
    if (hasFile) {
      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {manual.title || manual.file_name}
            </Typography>
            {canDownload && (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload(manual)}
              >
                {intl.get("helpCenter.downloadFile").d("Download File")}
              </Button>
            )}
          </Box>

          <DmsFileViewer
            filePk={manual.file_pk}
            fileName={manual.file_name}
            height="60vh"
          />
        </Box>
      );
    }

    // Render HTML content for single manual
    const anchors = manual?.anchors || [];
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={anchors.length > 0 ? 3 : 12}>
          {anchors.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {intl.get("helpCenter.manualTOC").d("Manual Sections")}
              </Typography>
              <List dense sx={{ maxHeight: "60vh", overflowY: "auto" }}>
                {anchors.map((anchor) => (
                  <ListItemButton
                    key={anchor.id}
                    selected={anchor.id === anchorId}
                    onClick={() => onSelectAnchor(anchor.id)}
                  >
                    <ListItemText primary={anchor.name} />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </Grid>
        <Grid item xs={12} md={anchors.length > 0 ? 9 : 12}>
          <Box sx={{ ...styles.manualContainer, ...styles.htmlStyles }}>
            <div dangerouslySetInnerHTML={createSafeHtml(manual.html)} />
          </Box>
        </Grid>
      </Grid>
    );
  }

  // Multiple manuals - show cards grid
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {intl.get("helpCenter.availableManuals").d("Available Manuals")}
      </Typography>
      <Grid container spacing={2}>
        {manuals.map((manual, index) => (
          <Grid item xs={12} sm={6} md={4} key={manual.file_pk || index}>
            <ManualCard
              manual={manual}
              onView={setSelectedManual}
              onDownload={handleDownload}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
